<template>
  <div>
    <!-- Top navbar -->
    <nav class="navbar navbar-top navbar-expand-md bg-white border-bottom" id="navbar-main">
      <div class="container-fluid">
        <router-link :to="{name: 'openSession'}" class="h4 mb-0 text-uppercase d-none d-lg-inline-block">
          Welcome
        </router-link>
        <div v-if="adminServiceProviderName" class="dashboard-provider-pill ml-auto mr-3">
          {{ adminServiceProviderName }}
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">

      <!-- Row 1: Quick Actions -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card dashboard-card shadow-sm">
            <div class="card-body py-3">
              <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem;">
                <span class="text-muted text-sm font-weight-bold text-uppercase mr-2">Quick Actions</span>
                <router-link :to="{ name: 'openSession', query: { new: '1' } }" class="btn btn-sm btn-primary">
                  <i class="fas fa-door-open mr-1"></i> Open a Session
                </router-link>
                <router-link :to="{ name: 'completedSession' }" class="btn btn-sm btn-outline-primary">
                  <i class="fas fa-history mr-1"></i> View Closed Sessions
                </router-link>
                <router-link :to="{ name: 'manageMobileUsers', query: { new: '1' } }" class="btn btn-sm btn-outline-secondary">
                  <i class="fas fa-user-plus mr-1"></i> Create Mobile User
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2: Stat Cards -->
      <div class="row align-items-stretch mb-4">
        <div class="col-xl-3 col-lg-6 mb-4 mb-xl-0">
          <div class="card shadow dashboard-card admin-stat-card admin-stat-card--blue h-100">
            <div class="card-body">
              <div class="row align-items-center h-100">
                <div class="col">
                  <h5 class="card-title text-uppercase text-muted mb-0">Connected <br>Sites</h5>
                  <span class="h2 font-weight-bold mb-0">{{siteCount}}</span>
                </div>
                <div class="col-auto p-0">
                  <div class="icon icon-shape admin-stat-card__icon admin-stat-card__icon--blue">
                    <i class="far fa-building"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-lg-6 mb-4 mb-xl-0">
          <div class="card shadow dashboard-card admin-stat-card admin-stat-card--green h-100">
            <div class="card-body">
              <div class="row align-items-center h-100">
                <div class="col">
                  <h5 class="card-title text-uppercase text-muted mb-0">Open <br>Sessions</h5>
                  <span class="h2 font-weight-bold mb-0">{{openSessionCount}}</span>
                </div>
                <div class="col-auto p-0">
                  <div class="icon icon-shape admin-stat-card__icon admin-stat-card__icon--green">
                    <i class="fas fa-door-open"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-lg-6 mb-4 mb-xl-0">
          <div class="card shadow dashboard-card admin-stat-card admin-stat-card--blue h-100">
            <div class="card-body">
              <div class="row align-items-center h-100">
                <div class="col">
                  <h5 class="card-title text-uppercase text-muted mb-0">Completed <br>Sessions</h5>
                  <span class="h2 font-weight-bold mb-0">{{completedCount}}</span>
                </div>
                <div class="col-auto p-0">
                  <div class="icon icon-shape admin-stat-card__icon admin-stat-card__icon--blue">
                    <i class="fas fa-door-closed"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-lg-6 mb-4 mb-xl-0">
          <div class="card shadow dashboard-card admin-stat-card admin-stat-card--green h-100">
            <div class="card-body">
              <div class="row align-items-center h-100">
                <div class="col">
                  <h5 class="card-title text-uppercase text-muted mb-0">Meter <br>Allowance</h5>
                  <span class="h2 font-weight-bold mb-0">{{meterAllowance}}</span>
                  <b-progress v-if="meterAllowanceParsed" :value="meterAllowanceParsed.current" :max="meterAllowanceParsed.max"
                    :variant="meterAllowanceColor" class="mt-3" style="height: 8px;"></b-progress>
                </div>
                <div class="col-auto p-0">
                  <div class="icon icon-shape admin-stat-card__icon admin-stat-card__icon--green">
                    <i class="fas fa-tachometer-alt"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 3: Active Sessions + Active Surveyor -->
      <div class="row">
        <!-- Active Sessions -->
        <div class="col-xl-6 mb-5 mb-xl-0">
          <div class="card shadow dashboard-card">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
                    <h3 class="mb-0 mr-2">Open Sessions</h3>
                    <div class="d-inline-flex align-items-center flex-wrap" style="gap: 0.25rem;">
                      <span class="text-sm text-muted mr-1">Filter:</span>
                      <button type="button" class="btn btn-sm rounded-pill px-3"
                        :class="filterAllSession === 'all' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="filterAllSession = 'all'">All</button>
                      <button type="button" class="btn btn-sm rounded-pill px-3"
                        :class="filterAllSession === 'open' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="filterAllSession = 'open'">Open</button>
                      <button type="button" class="btn btn-sm rounded-pill px-3"
                        :class="filterAllSession === 'completed' ? 'btn-primary' : 'btn-outline-primary'"
                        @click="filterAllSession = 'completed'">Completed</button>
                    </div>
                  </div>
                </div>
                <div class="col-auto">
                  <router-link :to="{name: 'openSession'}" class="btn btn-sm btn-primary">
                    See all ({{ completedSessionHomeDetails.open_session ? completedSessionHomeDetails.open_session.length : 0 }})
                  </router-link>
                </div>
              </div>
            </div>
            <div class="table-responsive dashboard-table-scroll">
              <table class="table align-items-center table-flush">
                <thead class="thead-light">
                  <tr>
                    <th scope="col">Site &amp; Date</th>
                    <th scope="col">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(session, index) in sortedCompletedSessions" :key="index"
                    v-show="filterAllSession === 'all' ? true : filterAllSession === 'open' ? session.count < session.total_count : session.count === session.total_count">
                    <td>
                      <div class="d-flex align-items-center">
                        <b-badge
                          v-b-tooltip.hover="serviceTypeIcon(session.service_type).label"
                          :variant="serviceTypeIcon(session.service_type).variant"
                          class="d-inline-flex align-items-center justify-content-center mr-2 flex-shrink-0"
                          style="width: 2.2rem; height: 2.2rem; border-radius: 50%; padding: 0;">
                          <i :class="'fas ' + serviceTypeIcon(session.service_type).icon" style="font-size: 1rem;"></i>
                        </b-badge>
                        <div>
                          <p class="text-gray mb-0 font-weight-bold" style="font-size: 0.875rem;">{{ session.site ? session.site.name : '' }}</p>
                          <small class="text-muted">Started at {{ session.created_at | fmtDate }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div v-if="session.count === session.total_count && session.total_count > 0" class="d-flex align-items-center">
                        <b-badge variant="success" class="mr-2"><i class="fas fa-check-circle"></i> Ready to close</b-badge>
                        <b-button size="sm" variant="success" @click="goToSession(session)">Close</b-button>
                      </div>
                      <div v-else>
                        <b-progress :value="session.count" :max="session.total_count" class="mb-1" style="height: 10px;"></b-progress>
                        <div class="d-flex align-items-center justify-content-between">
                          <small class="text-muted">{{ session.count }} / {{ session.total_count }}</small>
                          <b-button size="sm" variant="outline-info" class="ml-2 py-0" @click="goToSession(session)">
                            <i class="far fa-eye mr-1"></i>View
                          </b-button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Active Surveyor -->
        <div class="col-xl-6">
          <div class="card shadow dashboard-card">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Active Mobile Users</h3>
                </div>
                <div class="col-auto">
                  <router-link :to="{name: 'manageMobileUsers'}" class="btn btn-sm btn-primary">See all</router-link>
                </div>
              </div>
            </div>
            <div class="table-responsive dashboard-table-scroll">
              <table class="table align-items-center table-flush">
                <thead class="thead-light">
                  <tr>
                    <th scope="col">Name &amp; Email</th>
                    <th scope="col">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(user, index) in activeSurvoyor.slice(0, 5)" :key="index">
                    <td>
                      <p class="mb-0 font-weight-bold" style="font-size: 0.875rem;">{{user.name}}</p>
                      <small class="text-muted">{{user.email}}</small>
                    </td>
                    <td>{{ user.last_login ? $options.filters.fmtDateTime(user.last_login) : 'Never' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 4: Last Billing Cycle by Site -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="card shadow dashboard-card">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Last Billing Cycle by Site</h3>
                  <p class="text-muted text-sm mb-0 mt-1">Sites sorted by oldest closed session first — stale sites appear at the top.</p>
                </div>
                <div class="col-auto">
                  <router-link :to="{ name: 'completedSession' }" class="btn btn-sm btn-primary">
                    <i class="fas fa-history mr-1"></i> View History
                  </router-link>
                </div>
              </div>
            </div>

            <div v-if="lastBillingLoading" class="text-center py-5">
              <b-spinner variant="primary" small></b-spinner>
              <span class="text-muted ml-2 text-sm">Loading...</span>
            </div>

            <div v-else-if="lastBillingCycles.length === 0" class="text-center py-5">
              <i class="fas fa-calendar-times text-muted mb-2" style="font-size: 1.5rem; display: block;"></i>
              <p class="text-muted mb-0">No closed sessions recorded yet.</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table align-items-center table-flush">
                <thead class="thead-light">
                  <tr>
                    <th scope="col">Site</th>
                    <th scope="col">Service</th>
                    <th scope="col">Last Closed</th>
                    <th scope="col">Age</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in lastBillingCycles" :key="index" v-show="showAllBilling || index < 3">
                    <td class="font-weight-bold" style="font-size: 0.875rem;">{{ row.site_name }}</td>
                    <td>
                      <b-badge
                        :variant="serviceTypeIcon({ id: row.service_type_id }).variant"
                        class="d-inline-flex align-items-center"
                        style="gap: 0.3rem; padding: 0.35rem 0.6rem;"
                      >
                        <i :class="'fas ' + serviceTypeIcon({ id: row.service_type_id }).icon"></i>
                        {{ row.service_type }}
                      </b-badge>
                    </td>
                    <td class="text-sm">{{ row.last_close_date | fmtDate }}</td>
                    <td>
                      <span :class="billingAgeClass(row.last_close_date)" class="font-weight-bold text-sm">
                        {{ row.last_close_date | fromNow }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="!lastBillingLoading && lastBillingCycles.length > 3" class="card-footer bg-white border-top-0 text-center py-3">
              <button type="button" class="btn btn-sm btn-link text-primary font-weight-bold" @click="showAllBilling = !showAllBilling">
                <span v-if="!showAllBilling">
                  <i class="fas fa-chevron-down mr-1"></i> Show all {{ lastBillingCycles.length }} sites
                </span>
                <span v-else>
                  <i class="fas fa-chevron-up mr-1"></i> Show less
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
<script>
  import { mapGetters, mapActions } from 'vuex'
  import userData from '@/pages/Common/userData';
  import ApiServices from '@/services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

  export default {
    components: {
      userData,
    },
    data: () => ({
      openSessionCount: 0,
      completedCount: 0,
      siteCount: 0,
      adhocCount: 0,
      meterAllowance: 0,
      activeSurvoyor: [],
      filterAllSession: "all",
      lastBillingCycles: [],
      lastBillingLoading: true,
      showAllBilling: false
    }),
    computed: {
      ...mapGetters('coprops', ['coPropList', 'landingPageDetails', 'completedSessionHomeDetails']),
      ...mapGetters('survoyor', ['survoyorList']),
      sessionProgressPercent() {
        return (session) => {
          return session.total_count > 0 ? Math.round((session.count / session.total_count) * 100) : 0;
        };
      },
      meterAllowanceParsed() {
        if (typeof this.meterAllowance === 'string' && this.meterAllowance.includes('/')) {
          const [current, max] = this.meterAllowance.split('/').map(Number);
          return { current, max };
        }
        return null;
      },
      meterAllowanceColor() {
        if (!this.meterAllowanceParsed) return 'success';
        const pct = this.meterAllowanceParsed.current / this.meterAllowanceParsed.max;
        if (pct > 0.9) return 'danger';
        if (pct > 0.75) return 'warning';
        return 'success';
      },
      sortedCompletedSessions() {
        const sessions = this.completedSessionHomeDetails.open_session || [];
        return [...sessions].sort((a, b) => {
          const aCompleted = a.count === a.total_count;
          const bCompleted = b.count === b.total_count;
          if (aCompleted === bCompleted) return 0;
          return aCompleted ? -1 : 1;
        });
      },
      adminServiceProviderName() {
        const provider = this.landingPageDetails ? this.landingPageDetails.service_provider : '';
        if (!provider) return '';
        if (typeof provider === 'string') return provider;
        if (typeof provider === 'object') {
          return provider.name || provider.service_provider_name || provider.provider_name || '';
        }
        return '';
      }
    },
    methods: {
      ...mapActions('coprops', ['ALL_CO_PROP_LIST', 'OPEN_SESSION_LIST', 'LANDING_PAGE_COUNT', 'COMPLETED_SESSION_HOME']),
      ...mapActions('survoyor', ['SURVOYOR_LIST']),
      serviceTypeIcon(serviceType) {
        const id = serviceType.id || serviceType.service_type_id;
        if (id === 1) return { icon: 'fa-tint', variant: 'primary', label: 'Water' };
        if (id === 2) return { icon: 'fa-bolt', variant: 'warning', label: 'Electricity' };
        if (id === 3) return { icon: 'fa-fire', variant: 'danger', label: 'Gas' };
        return { icon: 'fa-circle', variant: 'secondary', label: serviceType.service_name || 'Unknown' };
      },
      goToSession(session) {
        const encoded = btoa(`${(session.site && session.site.id) || session.site_id}-${session.service_type.id}-${session.total_count}-${session.count}`);
        this.$router.push({ name: 'openSession', query: { type: encoded } });
      },
      getCoPropList () {
        this.ALL_CO_PROP_LIST().then(()=>{
          this.totalCoProps = this.coPropList.data.length
          this.getSurvoyorList()
        }).catch(() => {})
      },
      getSurvoyorList () {
        this.SURVOYOR_LIST().then(() => {
          this.survoyorList.data.forEach(element => {
            this.activeSurvoyor.push(element)
          });
          this.getCompletedSessionDetails()
        })
      },
      getLandingPageCount () {
        this.LANDING_PAGE_COUNT().then(() => {
          this.openSessionCount = this.landingPageDetails.openSessionCount
          this.completedCount = this.landingPageDetails.completedCount
          this.siteCount = this.landingPageDetails.siteCount
          this.adhocCount = this.landingPageDetails.adhocCount
          this.meterAllowance = this.landingPageDetails.meterAllowance
          this.getSurvoyorList()
        })
      },
      getCompletedSessionDetails () {
        this.COMPLETED_SESSION_HOME()
      },
      getLastSessionBySite() {
        ApiServices.getLastSessionBySite()
          .then(response => {
            this.lastBillingCycles = (response && response.data) || []
          })
          .catch(() => {})
          .finally(() => {
            this.lastBillingLoading = false
          })
      },
      billingAgeClass(dateStr) {
        const days = dayjs.utc().diff(dayjs.utc(dateStr), 'day')
        if (days > 90) return 'text-danger'
        if (days > 45) return 'text-warning'
        return 'text-success'
      }
    },
    created () {
      if (!localStorage.token) {
        localStorage.clear()
        next({
          name: 'adminLogin'
        })
      } else {
        if(localStorage.roleId == 1){
          this.$router.replace({name: "dashboard"});
        }
        this.getLandingPageCount();
        this.getLastSessionBySite();
      }
    }
  }
</script>
<style scoped>
.dashboard-provider-pill {
  display: inline-flex;
  align-items: center;
  min-height: 2.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #344767;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
}

.dashboard-card {
  border-radius: 1rem !important;
  overflow: hidden;
}

.dashboard-table-scroll {
  max-height: 320px;
  overflow-y: auto;
}

.admin-stat-card {
  border: 1px solid rgba(115, 199, 97, 0.12);
  box-shadow: 0 18px 38px rgba(31, 41, 55, 0.08) !important;
}

.admin-stat-card .card-body {
  position: relative;
  z-index: 1;
}

.admin-stat-card::before {
  content: "";
  position: absolute;
  inset: 0;
  opacity: 1;
  pointer-events: none;
}

.admin-stat-card--blue::before {
  background:
    radial-gradient(circle at top right, rgba(76, 182, 255, 0.2), transparent 32%),
    linear-gradient(135deg, rgba(76, 182, 255, 0.12) 0%, rgba(255, 255, 255, 0.96) 56%, rgba(115, 199, 97, 0.08) 100%);
}

.admin-stat-card--green::before {
  background:
    radial-gradient(circle at top right, rgba(115, 199, 97, 0.2), transparent 32%),
    linear-gradient(135deg, rgba(115, 199, 97, 0.12) 0%, rgba(255, 255, 255, 0.96) 56%, rgba(76, 182, 255, 0.08) 100%);
}

.admin-stat-card__icon {
  color: #fff;
  border-radius: 50%;
  box-shadow: 0 10px 22px rgba(31, 41, 55, 0.12);
}

.admin-stat-card__icon--blue {
  background: linear-gradient(135deg, #4cb6ff 0%, #3097ea 100%);
}

.admin-stat-card__icon--green {
  background: linear-gradient(135deg, #73c761 0%, #4fae3d 100%);
}

::v-deep .admin-stat-card .progress {
  background: rgba(52, 71, 103, 0.08);
}

@media (max-width: 767.98px) {
  .dashboard-provider-pill {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/Home.vue